import {
  ComponentType,
  createContext,
  forwardRef,
  ReactNode,
  useContext,
} from 'react';
import {
  Link as MuiLink,
  LinkProps as MuiLinkProps,
} from '@rossum/ui/material';

export type To =
  | string
  | {
      pathname?: string;
      search?: string;
      hash?: string;
      state?: unknown;
    };

export type LinkComponentProps = Omit<MuiLinkProps, 'href' | 'component'> & {
  to: To;
};

export type LinkComponent = ComponentType<LinkComponentProps>;

export type PromptComponentProps = {
  when: boolean;
  message: string | ((nextLocation: To) => string | boolean);
};

export type PromptComponent = ComponentType<PromptComponentProps>;

// Explicit list of every translation key the UI library expects the consumer
// to translate. The consumer's `intl.formatMessage` must accept these (any
// real `IntlShape` does, structurally).
type FilterShortcutKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'lastSevenDays'
  | 'lastThirtyDays'
  | 'lastNintyDays';

type AnnotationStatusKey =
  | 'created'
  | 'exporting'
  | 'deleted'
  | 'confirmed'
  | 'exported'
  | 'failedExport'
  | 'failedImport'
  | 'reviewing'
  | 'toReview'
  | 'importing'
  | 'split'
  | 'rejected'
  | 'postponed'
  | 'inWorkflow'
  | 'notExtracted';

export type RequiredMessageId =
  | 'component.auroraChip.label'
  | 'component.betaChip.label'
  | 'component.breadcrumbs.tooltip'
  | 'component.deprecatedChip.label'
  | 'component.internalChip.label'
  | 'component.internalChip.tooltip'
  | 'components.annotationOverview.pageCount'
  | 'components.clipboardButton.copy'
  | 'components.confirmationDialog.cancel'
  | 'components.confirmationDialog.confirm'
  | 'components.dashboard.queues.search'
  | 'components.deleteConfirmationDialog.cancel'
  | 'components.deleteConfirmationDialog.confirm'
  | 'components.emailResponseForm.fields.edit.bcc.label'
  | 'components.emailResponseForm.fields.edit.buttons.blindCopy.label'
  | 'components.emailResponseForm.fields.edit.buttons.copy.label'
  | 'components.emailResponseForm.fields.edit.cc.label'
  | 'components.emailResponseForm.fields.edit.to.label'
  | 'components.engineCardQueues.more'
  | 'components.filterPanel.filterMultiSelect.empty.noOptions'
  | 'components.filterPanel.filterMultiSelect.empty.search'
  | 'components.leavingDialog.continueEditing'
  | 'components.leavingDialog.description'
  | 'components.leavingDialog.discard'
  | 'components.leavingDialog.title'
  | 'components.userAvatar.unknown'
  | 'containers.annotationList.paginatorSelector.unit'
  | 'containers.documents.missingFileName'
  | 'containers.filtering.buttons.add'
  | 'containers.filtering.buttons.apply'
  | 'containers.filtering.buttons.back'
  | 'containers.filtering.buttons.cancel'
  | 'containers.filtering.buttons.clear'
  | 'containers.filtering.buttons.selectAll'
  | 'containers.filtering.chip.invalidValue'
  | 'containers.filtering.chip.invalidValueTooltip'
  | 'containers.filtering.helperText.pasteValues'
  | 'containers.filtering.placeholder.filterBy'
  | 'containers.settings.queues.email.trialLimited.upgradeChip.label'
  | 'containers.settings.queues.queue.settings.expandDescription'
  | 'features.documentList.preview.error.text'
  | 'features.documentList.preview.error.title'
  | 'features.formulas.suggester.menuListAnnotationVars.noResults.description'
  | 'features.formulas.suggester.menuListAnnotationVars.noResults.title'
  | 'features.formulas.suggester.menuListDatasetColumns.noResults.description'
  | 'features.formulas.suggester.menuListDatasetColumns.noResults.title'
  | 'features.formulas.suggester.menuListFields.noResults.description'
  | 'features.formulas.suggester.menuListFields.noResults.title'
  | 'features.formulas.suggester.search.placeholder'
  | 'features.reporting.currentSummary'
  | 'features.rules.copilotClose'
  | 'features.rules.copilotProcessing'
  | `containers.filtering.shortcuts.${FilterShortcutKey}`
  | `containers.annotationList.statuses.${AnnotationStatusKey}`;

type FormatXMLElementFn = (chunks: ReactNode[]) => ReactNode;

export type IntlAdapter = {
  formatMessage: (
    descriptor: { id: RequiredMessageId; defaultMessage?: string },
    values?: Record<string, ReactNode | FormatXMLElementFn>
  ) => string;
};

type ComponentsContextValue = {
  LinkComponent: LinkComponent;
  PromptComponent: PromptComponent | null;
  // Returns a value that changes whenever the host app considers the user to
  // have navigated to a different page. Components use it as an effect dep
  // to refresh per-page state. Default is a constant — fine for static apps.
  useRouteKey: () => string;
  intl: IntlAdapter;
};

const toHref = (to: To): string => {
  if (typeof to === 'string') return to;
  const { pathname = '', search = '', hash = '' } = to;
  return `${pathname}${search}${hash}`;
};

// forwardRef so the ref MUI attaches (e.g. Tooltip's popper anchor, when a Chip
// or Button renders as this link) reaches the underlying anchor element.
const DefaultLinkComponent: LinkComponent = forwardRef(
  ({ to, ...rest }, ref) => <MuiLink ref={ref} href={toHref(to)} {...rest} />
);

const defaultIntl: IntlAdapter = {
  formatMessage: ({ id, defaultMessage }) => defaultMessage ?? id,
};

const defaultContextValue: ComponentsContextValue = {
  LinkComponent: DefaultLinkComponent,
  PromptComponent: null,
  useRouteKey: () => '',
  intl: defaultIntl,
};

const ComponentsContext =
  createContext<ComponentsContextValue>(defaultContextValue);

type ComponentsContextProviderProps = Partial<ComponentsContextValue> & {
  children: ReactNode;
};

export const ComponentsContextProvider = ({
  children,
  ...overrides
}: ComponentsContextProviderProps) => (
  <ComponentsContext.Provider value={{ ...defaultContextValue, ...overrides }}>
    {children}
  </ComponentsContext.Provider>
);

export const useComponentsContext = () => useContext(ComponentsContext);

export const useIntl = (): IntlAdapter => useComponentsContext().intl;
